import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEmployees, getEmployeeBankAccounts } from 'services/employees';
import { updatePayroll, previewPayroll } from 'services/payrolls';

const resolvePaymentMethod = (emp, bankAccounts = []) => {
    if (bankAccounts.length > 0) return 'DirectDeposit';
    const raw = emp?.defaultPaymentMethod || emp?.paymentMethod || '';
    const val = raw.toLowerCase().replace(/[\s_-]/g, '');
    return val === 'directdeposit' ? 'DirectDeposit' : 'Check';
};

const getPeriodsPerYear = (payroll) => {
    const start = new Date(payroll.startDate);
    const end = new Date(payroll.endDate);
    const days = Math.max(1, (end - start) / (1000 * 60 * 60 * 24) + 1);
    return days <= 8 ? 52 : days <= 15 ? 26 : days <= 17 ? 24 : 12;
};

const W2EmployeesModal = ({ payroll, onClose }) => {
    const queryClient = useQueryClient();
    const [items, setItems] = useState({});
    const initializedRef = useRef(false);
    const selectAllRef = useRef(null);

    const { data: employeesRaw = [], isLoading } = useQuery({
        queryKey: ['employees'],
        queryFn: getEmployees,
    });

    const employees = useMemo(() => {
        const list = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []);
        return list.filter(e => e.active !== false);
    }, [employeesRaw]);

    const bankAccountQueries = useQueries({
        queries: employees.map(emp => ({
            queryKey: ['employee-bank-accounts', emp.id],
            queryFn: () => getEmployeeBankAccounts(emp.id),
            staleTime: 5 * 60 * 1000,
        })),
    });

    const empBankAccountsMap = useMemo(() => {
        const map = {};
        employees.forEach((emp, i) => {
            const raw = bankAccountQueries[i]?.data;
            map[emp.id] = Array.isArray(raw) ? raw : (raw?.data ?? raw?.items ?? []);
        });
        return map;
    }, [employees, bankAccountQueries]);

    const { data: previewData } = useQuery({
        queryKey: ['payroll-preview', payroll.id],
        queryFn: () => previewPayroll(payroll.id),
        staleTime: 0,
    });

    useEffect(() => {
        if (!initializedRef.current && previewData?.employees?.length) {
            initializedRef.current = true;
            const existing = {};
            previewData.employees.forEach(emp => {
                existing[emp.employeeId] = {
                    hours: emp.regularHours ?? '',
                    payRate: emp.payRate ?? '',
                    reimbursement: emp.reimbursementAmount ?? '',
                    paymentMethod: emp.paymentMethod ?? 'Check',
                };
            });
            setItems(existing);
        }
    }, [previewData]);

    const { mutate: save, isPending, isError, error } = useMutation({
        mutationFn: (employeeItems) => updatePayroll({
            payrollId: payroll.id,
            employeeItems,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payrolls'] });
            queryClient.removeQueries({ queryKey: ['payroll-preview', payroll.id] });
            onClose();
        },
    });

    const buildItem = (emp) => {
        const payType = (emp?.payType || 'salary').toLowerCase();
        const isHourly = payType === 'hourly';
        const annualRate = emp?.payRate ?? 0;
        const periodsPerYear = getPeriodsPerYear(payroll);
        const defaultRate = isHourly ? annualRate : (annualRate > 0 ? +(annualRate / periodsPerYear).toFixed(2) : '');
        return {
            hours: isHourly ? '' : null,
            payRate: defaultRate,
            reimbursement: '',
            paymentMethod: resolvePaymentMethod(emp, empBankAccountsMap[emp?.id] ?? []),
        };
    };

    const allSelected = employees.length > 0 && employees.every(e => !!items[e.id]);
    const someSelected = !allSelected && employees.some(e => !!items[e.id]);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    const handleSelectAll = () => {
        if (allSelected) {
            setItems({});
        } else {
            const next = {};
            employees.forEach(emp => {
                next[emp.id] = items[emp.id] ?? buildItem(emp);
            });
            setItems(next);
        }
    };

    const handleToggle = (id) => {
        setItems(prev => {
            const next = { ...prev };
            if (next[id]) {
                delete next[id];
            } else {
                const emp = employees.find(e => e.id === id);
                next[id] = buildItem(emp);
            }
            return next;
        });
    };

    const handleChange = (id, field, value) => {
        setItems(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value },
        }));
    };

    const handleSave = () => {
        const employeeItems = Object.entries(items).map(([employeeId, v]) => {
            const emp = employees.find(e => e.id === employeeId);
            const payType = (emp?.payType || 'salary').toLowerCase();
            const isHourly = payType === 'hourly';
            const hours = parseFloat(v.hours) || 0;
            const payRate = parseFloat(v.payRate) || 0;
            const reimbursement = parseFloat(v.reimbursement) || 0;
            return {
                employeeId,
                paymentMethod: v.paymentMethod || 'Check',
                payments: [{
                    regularHours: isHourly ? hours : undefined,
                }],
                reimbursements: reimbursement > 0
                    ? [{ amount: reimbursement, description: 'Reimbursement' }]
                    : undefined,
            };
        });
        save(employeeItems);
    };

    const formatDate = (d) => d
        ? new Date(d).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
        : '—';

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1060, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 860, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>

                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h5 className='mb-0 fw-semibold'>Add / Update W-2 Employee Payments</h5>
                        <p className='mb-0 mt-1' style={{ fontSize: 13, color: '#888' }}>
                            Pay Period: {formatDate(payroll.startDate)} – {formatDate(payroll.endDate)}
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: '24px' }}>
                    {isLoading ? (
                        <div className='text-center py-4'>Loading employees...</div>
                    ) : employees.length === 0 ? (
                        <div className='text-center py-4' style={{ color: '#888' }}>No active employees found.</div>
                    ) : (
                        <>
                            <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
                                Check the employees to include in this payroll and enter their payment details. Previously saved entries are pre-filled.
                            </p>
                            <div style={{ overflowX: 'auto' }}>
                                <table className='table table-sm' style={{ fontSize: 13, minWidth: 700 }}>
                                    <thead style={{ background: '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ width: 40 }}>
                                                <input
                                                    type='checkbox'
                                                    ref={selectAllRef}
                                                    checked={allSelected}
                                                    onChange={handleSelectAll}
                                                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                                                    title={allSelected ? 'Deselect all' : 'Select all'}
                                                />
                                            </th>
                                            <th>Employee</th>
                                            <th style={{ width: 80 }}>Pay Type</th>
                                            <th style={{ width: 90 }}>Hours</th>
                                            <th style={{ width: 110 }}>Pay Rate / Period ($)</th>
                                            <th style={{ width: 110 }}>Gross Pay ($)</th>
                                            <th style={{ width: 120 }}>Reimbursement ($)</th>
                                            <th style={{ width: 150 }}>Payment Method</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employees.map(emp => {
                                            const included = !!items[emp.id];
                                            const item = items[emp.id] || {};
                                            const payType = (emp.payType || 'salary').toLowerCase();
                                            const isHourly = payType === 'hourly';
                                            const hours = parseFloat(item.hours) || 0;
                                            const payRate = parseFloat(item.payRate) || 0;
                                            const grossPay = isHourly
                                                ? (hours * payRate).toFixed(2)
                                                : payRate.toFixed(2);

                                            const payTypeBadgeStyle = {
                                                background: isHourly ? '#e3f2fd' : '#f3e5f5',
                                                color: isHourly ? '#1565c0' : '#6a1b9a',
                                                fontWeight: 500, fontSize: 11, padding: '3px 7px', borderRadius: 4,
                                            };

                                            return (
                                                <tr key={emp.id} style={{ background: included ? '#f0f4ff' : '', transition: 'background 0.2s' }}>
                                                    <td>
                                                        <input
                                                            type='checkbox'
                                                            checked={included}
                                                            onChange={() => handleToggle(emp.id)}
                                                            style={{ cursor: 'pointer', width: 16, height: 16 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className='fw-medium'>{emp.firstName} {emp.lastName}</span>
                                                        {emp.jobTitle && <span style={{ color: '#888', marginLeft: 6, fontSize: 12 }}>({emp.jobTitle})</span>}
                                                    </td>
                                                    <td>
                                                        <span className='badge' style={payTypeBadgeStyle}>
                                                            {payType.charAt(0).toUpperCase() + payType.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {isHourly ? (
                                                            <input
                                                                type='number'
                                                                className='form-control form-control-sm'
                                                                placeholder='0'
                                                                min='0'
                                                                disabled={!included}
                                                                value={item.hours ?? ''}
                                                                onChange={e => handleChange(emp.id, 'hours', e.target.value)}
                                                            />
                                                        ) : (
                                                            <span style={{ color: '#aaa', fontSize: 12 }}>N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            className='form-control form-control-sm'
                                                            placeholder='0.00'
                                                            min='0'
                                                            step='0.01'
                                                            disabled={!included}
                                                            value={item.payRate ?? ''}
                                                            onChange={e => handleChange(emp.id, 'payRate', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type='text'
                                                            className='form-control form-control-sm'
                                                            readOnly
                                                            value={included ? grossPay : ''}
                                                            style={{ background: '#f0f4ff', color: '#1a3a8f', fontWeight: 600, cursor: 'default' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            className='form-control form-control-sm'
                                                            placeholder='0.00'
                                                            min='0'
                                                            step='0.01'
                                                            disabled={!included}
                                                            value={item.reimbursement ?? ''}
                                                            onChange={e => handleChange(emp.id, 'reimbursement', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            className='form-select form-select-sm'
                                                            disabled={!included}
                                                            value={item.paymentMethod ?? resolvePaymentMethod(emp, empBankAccountsMap[emp.id] ?? [])}
                                                            onChange={e => handleChange(emp.id, 'paymentMethod', e.target.value)}
                                                        >
                                                            <option value='Check'>Check</option>
                                                            <option value='DirectDeposit'>Direct Deposit</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {isError && (
                                <p className='text-danger mt-2' style={{ fontSize: 13 }}>
                                    Failed to save employee payments. {error?.message}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button className='cancel-button px-4' onClick={onClose}>Cancel</button>
                    <button
                        className='button-orange px-5'
                        onClick={handleSave}
                        disabled={isPending || Object.keys(items).length === 0}
                    >
                        {isPending ? 'Saving...' : 'Save Employee Payments'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default W2EmployeesModal;
